/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import FeatherIcon from "components/FeatherIcon";
import BookingPackagesViewFooterPrices from "./BookingPackagesViewFooterPrices";

interface BookingPackagesViewFooterProps {
    onBack: () => void;
    onContinue: () => void;
    disabled: boolean;
    continueLabel?: string;
    /**
     * "detailed" — Family / Events look (may show a discount block).
     * "basic" — Basic credits look (slightly different totals styling, no discount).
     */
    variant?: "detailed" | "basic";
    /** Alt text for the discount badge image. */
    discountImageAlt?: string;
    /** Extra class(es) for the root element, e.g. the view's marker class. */
    className?: string;
    continueDescription?: React.ReactNode;
}

/**
 * Shared footer for the packages / credits booking views: a back button, the
 * price summary and the continue button.
 */
const BookingPackagesViewFooter = ({
    onBack,
    onContinue,
    disabled,
    continueLabel,
    variant = "detailed",
    discountImageAlt,
    className,
    continueDescription,
}: BookingPackagesViewFooterProps) => {
    const {t} = useTranslation();

    return (
        <div
            className={clsx(
                className,
                "flex flex-wrap flex-row items-center justify-between w-full rounded-b-xl bg-white px-2 lg:px-3 xl:px-6 pb-2 xl:py-3 lg:min-h-[100px] xl:min-h-[120px] gap-2 lg:gap-2 xl:gap-4"
            )}
        >
            <button
                className={
                    "order-2 lg:order-1 font-bold text-base sm:text-xl rounded-md border-2 sm:leading-5 w-full xs:w-auto basis-auto grow-0 bg-white !text-mid-grey border-mid-grey px-4 sm:px-3 py-3 flex flex-row items-center justify-center"
                }
                onClick={onBack}
            >
                <FeatherIcon className={"size-[24px]"} name={"corner-up-left"} />
            </button>

            <BookingPackagesViewFooterPrices variant={variant} discountImageAlt={discountImageAlt} />

            <div className={clsx("flex flex-col gap-y-1 xs:items-end order-3 lg:order-3 w-full xs:w-auto", continueLabel && "max-w-full xs:max-w-[312px]")}>
                <button
                    className={clsx(
                        "text-white font-bold rounded-md border-2 sm:leading-5 w-full basis-auto grow-0 py-3 flex flex-row items-center justify-center xl:min-w-[180px] gap-2",
                        continueLabel ? "text-base px-3" : "xs:w-auto text-base sm:text-xl px-6 sm:px-8",
                        disabled ? "bg-[#878181] border-mid-grey text-white cursor-not-allowed" : "bg-[#3AA90D] border-[#097907] text-white"
                    )}
                    disabled={disabled && !continueLabel} // special case for Competitions warning
                    onClick={() => onContinue()}
                    type={"submit"}
                >
                    <span className={"text-center w-full"}>{continueLabel || t("Weiter")}</span>
                    <FeatherIcon className={"size-[24px]"} name={continueLabel && continueLabel.includes("Choose") ? "arrow-up" : "arrow-right"} />
                </button>
                {continueDescription && <div className={"text-xs font-semibold leading-tight text-left mt-1 text-black"}>{continueDescription}</div>}
            </div>
        </div>
    );
};

export default BookingPackagesViewFooter;
